/**
 * Data for the naming dialog's thumbnail picker.
 *
 * Built-in previews are documents generated from the canonical base-template
 * id, not checked-in pictures. `instantiateBaseRoomTemplate` duplicates that
 * same document, so changing a shape changes its picker preview automatically
 * and a thumbnail cannot silently drift away from the room it names.
 */
import { readSketchMeta } from '../store/sketch-store.js';
import {
  BASE_ROOM_TEMPLATES,
  baseRoomTemplateDocument,
} from '../templates/base-room-templates.js';

/**
 * Return the blank, built-in, and saved choices in display order.
 *
 * `previewDocument` deliberately rides on each item. The renderer never looks
 * a preview up by label or list position, both of which could pair a name with
 * the wrong geometry after a reorder.
 *
 * @param {object[]} personalDocs saved template documents, already sorted
 * @returns {Array<{id:string,name:string,group:string,detail:string,previewDocument:object}>}
 */
export function identityTemplatePickerItems(personalDocs = []) {
  const items = [{
    id: '',
    name: 'Blank space',
    group: '',
    detail: 'Start without walls',
    previewDocument: { walls: [] },
  }];

  for (const template of BASE_ROOM_TEMPLATES) {
    items.push({
      id: template.id,
      name: template.name,
      group: 'Built-in templates',
      detail: 'Built in · available offline',
      previewDocument: baseRoomTemplateDocument(template.id),
    });
  }

  for (const doc of Array.isArray(personalDocs) ? personalDocs : []) {
    const meta = readSketchMeta(doc);
    if (!meta.isTemplate) continue;
    items.push({
      id: meta.id,
      name: meta.templateName || 'Untitled template',
      group: 'Saved templates',
      detail: meta.updatedAt ? new Date(meta.updatedAt).toLocaleString() : 'Saved template',
      previewDocument: doc,
    });
  }

  return items;
}
